import parseFraction from './parseFraction';
import {
    isForecastMultiple,
    getReturnsForMultiples,
    getReturnsForMultiple,
} from './multiplesReturns'
import createCurrencyFormatter from './formFormatters';
import formatReturns from './formatReturns';
import PRICE_TYPES from './priceTypes'

function singleIsSp(priceType, isSpOnly) {
    return priceType === PRICE_TYPES.SP || isSpOnly === true
}

function stakeIsValid(stake) {
    const parsedStake = parseFloat(stake, 10)
    return !isNaN(parsedStake) && parsedStake > 0
}

/**
 * Calculate estimated return
 *
 * @param {Number} stake - stake for race
 * @param {String} fractionalPrice - representation of fractiolnal price, e.g. 'Evs', '1/2', '1/4'
 * @param {String} ewFractional - representation of eachWay fractional value
 * for each way bet. Should be empty if do not need to calculate each way stake.
 * @param {Boolean} placeOnly - return just the place returns for an EW bet
 *
 * @return {Number} - estimated return
 */
export function calculateReturn(stake, fractionalPrice, ewFractional, placeOnly) {
    const parsedFractional = fractionalPrice === 'Evs' ? 1 : parseFraction(fractionalPrice);
    let result = (parsedFractional * stake) + stake;
    if (ewFractional) {
        const parsedEWFractional = parseFraction(ewFractional);
        const placeReturns = (parsedFractional * stake * parsedEWFractional) + stake
        if (placeOnly) {
            return placeReturns
        }
        result += placeReturns
    }
    return result;
}

/**
 * Calculate the returns for a single bet
 *
 * @param  {Object} bet (from selectedBets)
 *
 * @return {Number}
 */
function getReturnsForSingle(bet) {
    const {
        stake,
        isEachWay,
        fractionalPrice,
        eachWayFraction,
        priceType,
        isSpOnly,
    } = bet

    // Any SP singles with no stake should be skipped
    // (Note that SP singles with a stake will be caught above)
    if (singleIsSp(priceType, isSpOnly)) {
        return 0
    }

    // Assume that all remaining cases are an Odds price
    const parsedStake = Number.parseFloat(stake) || 0
    const betReturn = calculateReturn(
        parsedStake,
        fractionalPrice,
        isEachWay && eachWayFraction,
    )

    return betReturn
}

/**
 * Calculate the total returns for all single selections in the
 * user's betslip.
 *
 * @param  {Array} selectedBets
 *
 * @return {Number}
 */
function getReturnsForSingles(selectedBets) {
    if (selectedBets.length === 0) {
        return 0
    }

    return selectedBets.reduce((runningTotal, bet) => {
        const betReturn = getReturnsForSingle(bet)
        return runningTotal + betReturn
    }, 0)
}

/**
 * Cap returns at 2m, apply local formatting
 *
 * @param  {Number returns
 * @param  {String} currencySign
 *
 * @return {String}
 */
function postProcessReturns(returns, currencySign) {
    const MAX_RETURNS = 10000000 // Oh boy!
    const total = returns > MAX_RETURNS ? MAX_RETURNS : returns
    const formattedReturns = formatReturns(total)
    const currencyFormattedReturns = createCurrencyFormatter(
        currencySign,
    )(formattedReturns)

    return currencyFormattedReturns
}

const determineTotalReturns = (
    summed,
    freeBetIsSelected,
    freeBetAmount,
    selectedBets,
    multiples,
) => {
    // If free bet is not selected, or should not be selected, skip this
    if (!freeBetIsSelected || !freeBetAmount || selectedBets.length !== 1 || multiples.length > 0) {
        return summed
    }

    // If we are in a free bet scenario, subtract the free bet amount
    let total = summed
    const bet = selectedBets[0]
    const stake = parseFloat(bet.stake, 10) // can be NaN (no stake)

    if (isNaN(stake)) {
        return summed
    }

    // Double the base stake when EW
    const totalStake = (bet.isEachWay)
        ? stake * 2
        : stake

    // Subtract the free stake from the total
    const freeStake = (freeBetAmount >= totalStake)
        ? totalStake
        : freeBetAmount

    total -= freeStake

    return total
}

/**
 * Calculates the estimated returns for ALL bets and formats it.
 * Includes single bets, multiples and forecast bets.
 *
 * Note that use of Each Way (EW) and Starting Price (SP) are important
 * and in scenarios where we cannot calculate the returns on the client,
 * we display "N/A".
 *
 * @param  {Array} selectedBets (contains diffusion EW data)
 * @param  {Array} multiples (contains backend multiples data)
 * @param  {String} currencySign
 * @param  {Boolean} freeBetIsSelected
 * @param  {Number} freeBetAmount
 *
 * @return {String} e.g. '£22.50'
 */
export function getTotalEstimatedReturnText(
        selectedBets,
        multiples,
        currencySign,
        freeBetIsSelected,
        freeBetAmount,
    ) {
    // Always display 'N/A' if any staked SP bets are detected
    const stakedSpSingles = selectedBets.some((bet) => {
        const {
            priceType,
            isSpOnly,
            stake,
        } = bet
        return singleIsSp(priceType, isSpOnly) && stakeIsValid(stake)
    })

    if (stakedSpSingles) {
        return 'N/A'
    }

    const includedSelectedBets = selectedBets.filter(
        bet => bet.includeInMultiples,
    )
    const spMultiplesSelection = includedSelectedBets.find(
        bet => singleIsSp(bet.priceType, bet.isSpOnly),
    )

    // stakedSpMultiples or stakedForecasts = N/A
    const stakedMultiplesWithNoReturn = multiples.some((multiple) => {
        const {
            betType,
            stakeValue,
        } = multiple
        const hasValidStake = stakeIsValid(stakeValue)
        const isStakedWithSp = hasValidStake && spMultiplesSelection
        const isStakedForecast = hasValidStake && isForecastMultiple(betType)
        return isStakedWithSp || isStakedForecast
    })

    if (stakedMultiplesWithNoReturn) {
        return 'N/A'
    }

    // Otherwise, calculate the returns for all singles & multiples
    // Plus, take into account free bets
    const singlesTotal = getReturnsForSingles(selectedBets)
    const multiplesTotal = getReturnsForMultiples(includedSelectedBets, multiples)
    const summed = singlesTotal + multiplesTotal
    const total = determineTotalReturns(
        summed,
        freeBetIsSelected,
        freeBetAmount,
        selectedBets,
        multiples,
    )

    return postProcessReturns(total, currencySign)
}

/**
 * Gets the estimated returns text for any single bet.
 *
 * @param  {Object} bet (from selectedBets)
 * @param  {String} currencySign
 *
 * @return {String} e.g. '£25.53' or 'N/A'
 */
export function getSingleEstimatedReturnText(bet, currencySign) {
    const {
        priceType,
        isSpOnly,
        stake,
    } = bet
    const isSp = singleIsSp(priceType, isSpOnly)
    const hasValidStake = stakeIsValid(stake)

    if (isSp && hasValidStake) {
        return 'N/A'
    }

    const total = getReturnsForSingle(bet)

    return postProcessReturns(total, currencySign)
}

/**
 * Gets the estimated returns text for any multiple bet.
 *
 * @param  {Array} selectedBets
 * @param  {Object} multiple (from build bets)
 * @param  {String} currencySign
 *
 * @return {String} e.g. '£25.53' or 'N/A'
 */
export function getMultipleEstimatedReturnText(selectedBets, multiple, currencySign) {
    const total = getReturnsForMultiple(selectedBets, multiple)

    // If any SP is selected in the multiple's corresponding selections, return N/A
    const spBet = selectedBets.find(
        bet => bet.includeInMultiples && singleIsSp(bet.priceType, bet.isSpOnly),
    )
    if (spBet) {
        return 'N/A'
    }

    return postProcessReturns(total, currencySign)
}



// WEBPACK FOOTER //
// /src/js/common/utils/estimatedReturn.js